import {all, call, fork, put, take} from "redux-saga/effects";

import {Project} from "../../domain/model/project";
import {
    IProjectRepository,
    TGetAllBySearchParams,
    TGetCountBySearchParams
} from "../../domain/repository/project_repository";
import {
    TCompanyIDWithProjectID,
    TCompanyWithProjectID,
    ICompanyRepository,
} from "../../domain/repository/company_repository";
import {Pager} from "../../domain/model/common";

import {createProjectAPI} from "../../infra/api/service/project_api";
import {createCompanyAPI} from "../../infra/api/service/company_api";
import {createApiClient, IApiClient} from "../../infra/api/client";

import {
    SearchProjectListByCustomerResponse,
    GetProjectListByCustomerResponse,
    GetProjectCountByCustomerResponse, GetEntryNumOrderProjectListByCustomerResponse,
} from "../response/project_response";

import {
    CustomerProjectListActionType,
    createCustomerProjectListActionCreator,
    ICustomerProjectListActionCreator,
    IRequestGetProjectListAction,
    IRequestSearchProjectListAction, IRequestGetEntryNumOrderProjectListAction,
} from "../action/customer_projects_action";
import {handleErrorForHandler} from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const actionCreator: ICustomerProjectListActionCreator = createCustomerProjectListActionCreator();

function* handleGetEntryNumOrderProjectListInCustomerProjectList() {
    while (true) {
        try {
            const action: IRequestGetEntryNumOrderProjectListAction = yield take(CustomerProjectListActionType.REQUEST_GET_ENTRY_NUM_ORDER_PROJECT_LIST);
            const [newlyArrivedProjects, entryNumOrderProjects] = yield all([
                call(getNewlyArrivedProjectList, action.item.pager),
                call(getEntryNumOrderProjectList, action.item.pager)
            ])

            const companyIDWithNewlyArrivedProjectIDList: TCompanyIDWithProjectID[] = yield call(createCompanyIdWithProjectIdList, newlyArrivedProjects);
            const companyIDWithEntryNumOrderProjectIDList: TCompanyIDWithProjectID[] = yield call(createCompanyIdWithProjectIdList, entryNumOrderProjects);
            const [companiesWithNewlyArrivedProjectID, companiesWithEntryNumOrderProjectID] = yield all([
                call(getMaskCompanyList, companyIDWithNewlyArrivedProjectIDList),
                call(getMaskCompanyList, companyIDWithEntryNumOrderProjectIDList)
            ])

            const res: GetEntryNumOrderProjectListByCustomerResponse = new GetEntryNumOrderProjectListByCustomerResponse();
            res.newlyArrivedProjects = newlyArrivedProjects;
            res.companiesWithNewlyArrivedProjectID = companiesWithNewlyArrivedProjectID
            res.entryNumOrderProjects = entryNumOrderProjects;
            res.companiesWithEntryNumOrderProjectID = companiesWithEntryNumOrderProjectID;
            yield put(actionCreator.callbackGetEntryNumOrderProjectListAction(true, res))
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(actionCreator.callbackGetEntryNumOrderProjectListAction(false));
        }
    }
}

function* handleGetProjectListInCustomerProjectList() {
    while (true) {
        try {
            const action: IRequestGetProjectListAction = yield take(CustomerProjectListActionType.REQUEST_GET_PROJECT_LIST);
            // ToDo: TotalCountsの取得時のBackendコストとの調整次第ではこのロジックを修正するかもしれない
            const [projectList, projectCount] = yield all([
                call(getProjectList, action.item.pager),
                call(getProjectCount)
            ])
            const companyIDWithProjectIDList: TCompanyIDWithProjectID[] = yield call(createCompanyIdWithProjectIdList, projectList);
            const companyWithProjectIDList: TCompanyWithProjectID[] = yield call(getMaskCompanyList, companyIDWithProjectIDList);
            const res: GetProjectListByCustomerResponse = new GetProjectListByCustomerResponse();
            res.projects = projectList;
            res.companiesWithProjectID = companyWithProjectIDList;
            res.projectCount = projectCount;
            yield put(actionCreator.callbackGetProjectListAction(true, res))
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(actionCreator.callbackGetProjectListAction(false));
        }
    }
}

// ToDo: 削除保留）TotalCountsの取得時のBackendコストを抑えるために、分離したがPager機能との整合性が複雑になるためREQUEST_GET_PROJECT_LIST時に実行されるように変更した
function* handleGetProjectCountInCustomerProjectList() {
    while (true) {
        try {
            yield take(CustomerProjectListActionType.REQUEST_GET_PROJECT_COUNT);
            const projectCount: number = yield call(getProjectCount);
            const res: GetProjectCountByCustomerResponse = new GetProjectCountByCustomerResponse();
            res.projectCount = projectCount
            yield put(actionCreator.callbackGetProjectCountAction(true, res))
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(actionCreator.callbackGetProjectCountAction(false))
        }
    }
}

function* handleSearchProjectListInCustomerProjectList() {
    while (true) {
        try {
            const action: IRequestSearchProjectListAction = yield take(CustomerProjectListActionType.REQUEST_SEARCH_PROJECT_LIST);

            const searchProjectListParams: TGetAllBySearchParams = {
                page: action.item.page,
                offset: action.item.offset,
                q: action.item.q,
                occupation: action.item.occupation,
                workingHourRange: action.item.workingHourRange,
                paymentRange: action.item.paymentRange,
                workingDuration: action.item.workingDuration,
                operatingDate: action.item.operatingDate,
            };

            const getCountBySearchParams: TGetCountBySearchParams = {
                q: action.item.q,
                occupation: action.item.occupation,
                workingHourRange: action.item.workingHourRange,
                paymentRange: action.item.paymentRange,
                workingDuration: action.item.workingDuration,
                operatingDate: action.item.operatingDate,
            };

            const [projectList, projectCount] = yield all([
                call(searchProjectList,searchProjectListParams),
                call(getCountBySearch,getCountBySearchParams)
            ])

            const companyIDWithProjectID: TCompanyIDWithProjectID[] = yield call(createCompanyIdWithProjectIdList, projectList);
            const companyWithProjectIDList: TCompanyWithProjectID[] = yield call(getMaskCompanyList, companyIDWithProjectID);
            const res: SearchProjectListByCustomerResponse = new SearchProjectListByCustomerResponse();
            res.projects = projectList;
            res.companiesWithProjectID = companyWithProjectIDList;
            res.projectCount = projectCount;
            yield put(actionCreator.callbackSearchProjectListAction(true, res))
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(actionCreator.callbackSearchProjectListAction(false));
        }
    }
}

const getProjectList = (pager: Pager): Promise<Project[]> => {
    return projectRepository.getAllByNewlyArrived(pager.page, pager.offset)
};

const getEntryNumOrderProjectList = (pager: Pager): Promise<Project[]> => {
    return projectRepository.getAllByEntryNumOrder(pager.page, pager.offset)
};

const getNewlyArrivedProjectList = (pager: Pager): Promise<Project[]> => {
    return projectRepository.getAllByNewlyArrived(pager.page, pager.offset)
};

const createCompanyIdWithProjectIdList = (projectList: Project[]): TCompanyIDWithProjectID[] => {
    return projectList.map((item: Project) => {
        const companyIDWithProjectID: TCompanyIDWithProjectID = {
            companyID: item.companyID,
            projectID: item.id,
        };
        return companyIDWithProjectID;
    })
};

const getMaskCompanyList = (ids: TCompanyIDWithProjectID[]): Promise<TCompanyWithProjectID[]> => {
    return companyRepository.getAllByIDsWithMask(ids);
};

const getProjectCount = (): Promise<number|Long> => {
    return projectRepository.getCountByNewlyArrived();
};

const getCountBySearch = (getCountBySearchParams: TGetCountBySearchParams): Promise<number | Long> => {
    return projectRepository.getCountBySearch(getCountBySearchParams);
};

const searchProjectList = (getAllBySearchParams: TGetAllBySearchParams): Promise<Project[]> => {
    return projectRepository.getAllBySearch(getAllBySearchParams);
};

export {
    handleGetEntryNumOrderProjectListInCustomerProjectList,
    handleGetProjectListInCustomerProjectList,
    handleSearchProjectListInCustomerProjectList,
    handleGetProjectCountInCustomerProjectList
}



// WEBPACK FOOTER //
// ./src/presentation/handler/customer_projects_handler.ts